"use client";

import { useCallback } from "react";
import { usePostHog } from "@posthog/next";
import type { AnalyticsProperties } from "./types";

export function useAnalytics() {
  const posthog = usePostHog();

  const trackEvent = useCallback(
    (event: string, properties?: AnalyticsProperties) => {
      // Log analytics events in development
      if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
        console.log("📊 Analytics Event:", { event, properties });
      }

      posthog?.capture(event, properties);
    },
    [posthog],
  );

  const trackWebAppButtonClick = useCallback(
    (properties: AnalyticsProperties) => {
      trackEvent("web_app_button_click", properties);
    },
    [trackEvent],
  );

  const identify = useCallback(
    (userId: string, traits?: AnalyticsProperties) => {
      posthog?.identify(userId, traits);
    },
    [posthog],
  );

  const reset = useCallback(() => {
    posthog?.reset();
  }, [posthog]);

  const captureException = useCallback(
    (error: unknown, source: string, properties?: AnalyticsProperties) => {
      const err = error instanceof Error ? error : new Error(String(error));
      posthog?.captureException(err, {
        $exception_source: source,
        ...(properties ?? {}),
      });
    },
    [posthog],
  );

  return {
    trackEvent,
    trackWebAppButtonClick,
    identify,
    reset,
    captureException,
  };
}
